import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  GetPublicRequest,
  RatePublicRequest,
  GetPublicRequestAttachments,
  GetPublicRequestAccruals,
  CreatePaymentLink,
  GetPublicRequestMeters,
  CreateMetersCheckRequest,
  PostMetersReadings,
  ReserveAttachments,
  AttachFiles,
} from '../../api/requests';
import { IRequestStatus } from './types';
import { SaveFileToFilesMS } from 'app/api/files';

type RequestStatus = 'idle' | 'loading' | 'success' | 'error';

export type MeterType =
  | 'ColdWaterAreaMeter'
  | 'HotWaterAreaMeter'
  | 'GasAreaMeter'
  | 'ElectricOneRateAreaMeter'
  | 'ElectricTwoRateAreaMeter'
  | 'ElectricThreeRateAreaMeter'
  | 'HeatAreaMeter'
  | 'HeatDistributorAreaMeter';

export interface IAttachments {
  id: string;
  file_name: string;
}

export interface IPublicRequestAccrualsData {
  sector_code: string;
  sector_code_ru: string;
  balance: number;
}

export interface IPublicRequestMetersData {
  check_date: string;
  id: string;
  is_automatic: boolean;
  last_readings: {
    date: string;
    value: string;
    estimated_value: number;
  }[];
  unit: string;
  serial_number: string;
  type: {
    type: MeterType;
    type_ru: string;
  };
}

export interface IPublicRequestData {
  tenant_phone: string;
  status: IRequestStatus;
  status_ru: string;
  request_number: string;
  address: string;
  description: string;
  dispatcher_phones: string[];
  created_at: string;
  attachments: IAttachments[];
  transfer_date: string | null;
  can_add_attachments: boolean;
  can_be_rated: boolean;
  planned_visit_time: string | null;
  rating: 1 | 2 | 3 | 4 | 5 | null;
  reason_of_abandonment: string | null;
  reason_of_transfer: string | null;
  time_of_execution_start: string | null;
  time_of_execution_end: string | null;
  work_description: string | null;
}

export interface IInitialState {
  publicId: string;
  showCallCard: boolean;
  showMetersResultModal: 'request' | 'readings' | null;
  showMetersReadingsModal: boolean;
  onLoadRequestData: boolean;
  onLoadAccrualsData: boolean;
  onLoadMetersData: boolean;
  onPostMetersReadingsStatus: boolean;
  attachmentsStatus: RequestStatus;
  requestData: IPublicRequestData;
  accrualsData: IPublicRequestAccrualsData[];
  metersData: IPublicRequestMetersData[];
  mobileAppModalOpen: boolean;
  metersCheckRequestLink: string | null;
}

export const initialState: IInitialState = {
  publicId: '',
  showCallCard: false,
  showMetersResultModal: null,
  showMetersReadingsModal: false,
  onLoadRequestData: false,
  onLoadAccrualsData: false,
  onLoadMetersData: false,
  onPostMetersReadingsStatus: false,
  attachmentsStatus: 'idle',
  requestData: {
    tenant_phone: '',
    status: 'accepted',
    status_ru: '',
    request_number: '',
    address: '',
    description: '',
    dispatcher_phones: [],
    created_at: '',
    attachments: [],
    transfer_date: null,
    can_add_attachments: false,
    can_be_rated: true,
    planned_visit_time: null,
    rating: null,
    reason_of_abandonment: null,
    reason_of_transfer: null,
    time_of_execution_start: null,
    time_of_execution_end: null,
    work_description: null,
  },
  accrualsData: [],
  metersData: [],
  mobileAppModalOpen: false,
  metersCheckRequestLink: null,
};

export const getPublicRequest = createAsyncThunk<any, string, any>(
  'request-single-page/getPublicRequest',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      return await GetPublicRequest(id);
    } catch (e) {
      // dispatch(showWarning());
      return rejectWithValue(e);
    }
  }
);

interface IRatePublicRequestParams {
  id: string;
  score: number;
}

export const ratePublicRequest = createAsyncThunk<
  any,
  IRatePublicRequestParams,
  any
>(
  'request-single-page/ratePublicRequest',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      return await RatePublicRequest(params.id, params.score);
    } catch (e) {
      // dispatch(showWarning());
      return rejectWithValue(e);
    }
  }
);

export const getPublicRequestAttachments = createAsyncThunk<
  IAttachments[],
  string,
  any
>(
  'request-single-page/getPublicRequestAttachments',
  async (id, { rejectWithValue }) => {
    try {
      return await GetPublicRequestAttachments(id);
    } catch (error) {
      // dispatch(showWarning());
      return rejectWithValue(error);
    }
  }
);

export type ReservationItem = {
  filename: string;
  file_id: string;
  shared_id: string;
};

export const reserveAttachments = createAsyncThunk<
  ReservationItem[],
  string[],
  any
>(
  'request-single-page/reserveAttachments',
  async (data, { rejectWithValue }) => {
    try {
      return await ReserveAttachments(data);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const attachFilesToPublicRequest = createAsyncThunk<
  any,
  { id: string; files: { file_id: string; filename: string }[] },
  any
>(
  'request-single-page/attachFilesToPublicRequest',
  async (data, { rejectWithValue }) => {
    try {
      return await AttachFiles(data.id, data.files);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const uploadAttachment = createAsyncThunk<
  any,
  { shared_id: string; file: File },
  any
>(
  'requests/uploadRequestFile',
  async ({ shared_id, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('importance', 'low');

      return await SaveFileToFilesMS(shared_id, formData);
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const getPublicRequestAccruals = createAsyncThunk<any, string, any>(
  'request-single-page/getPublicRequestAccruals',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      return await GetPublicRequestAccruals(id);
    } catch (e) {
      // dispatch(showWarning());
      return rejectWithValue(e);
    }
  }
);

interface ICreatePaymentLinkParams {
  id: string;
  sector_code: string;
}

export const createPaymentLink = createAsyncThunk<
  any,
  ICreatePaymentLinkParams,
  any
>(
  'request-single-page/createPaymentLink',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      return await CreatePaymentLink(params.id, params.sector_code);
    } catch (e) {
      // dispatch(showWarning());
      return rejectWithValue(e);
    }
  }
);

export const getPublicRequestMeters = createAsyncThunk<any, string, any>(
  'request-single-page/getPublicRequestMeters',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      return await GetPublicRequestMeters(id);
    } catch (e) {
      // dispatch(showWarning());
      return rejectWithValue(e);
    }
  }
);

interface ICreateMetersCheckRequestParams {
  id: string;
  data: {
    meter_id: string;
  }[];
}

export const createMetersCheckRequest = createAsyncThunk<
  any,
  ICreateMetersCheckRequestParams,
  any
>(
  'request-single-page/createMetersCheckRequest',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      return await CreateMetersCheckRequest(params.id, params.data);
    } catch (e) {
      // dispatch(showWarning());
      return rejectWithValue(e);
    }
  }
);

interface IPostMetersReadings {
  id: string;
  data: {
    meter_id: string;
    values: number[];
  }[];
}

export const postMetersReadings = createAsyncThunk<
  any,
  IPostMetersReadings,
  any
>(
  'request-single-page/postMetersReadings',
  async (params, { rejectWithValue }) => {
    try {
      return await PostMetersReadings(params.id, params.data);
    } catch (e) {
      // dispatch(showWarning());
      return rejectWithValue(e);
    }
  }
);

export const requestSinglePageSlice = createSlice({
  name: 'request-single-page',
  initialState,
  reducers: {
    savePublicId: (state, action) => {
      state.publicId = action.payload;
    },
    setShowCallCard: (state, action) => {
      state.showCallCard = action.payload;
    },
    setShowMetersResultModal: (state, action) => {
      state.showMetersResultModal = action.payload;

      if (action.payload === null) {
        state.metersCheckRequestLink = null;
      }
    },
    setShowMetersReadingsModal: (state, action) => {
      state.showMetersReadingsModal = action.payload;
    },
    setMobileAppModalOpen: (state, action: PayloadAction<boolean>) => {
      state.mobileAppModalOpen = action.payload;
    },
    setRequestRating: (state, action) => {
      state.requestData = { ...state.requestData, rating: action.payload };
    },
    clearMetersData: (state) => {
      state.metersData = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getPublicRequest.pending, (state, action) => {
      state.onLoadRequestData = true;
    });
    builder.addCase(getPublicRequest.fulfilled, (state, action) => {
      state.requestData = {
        ...action.payload,
        attachments: [],
      };
      state.onLoadRequestData = false;
    });
    builder.addCase(getPublicRequest.rejected, (state, action) => {
      state.onLoadRequestData = false;
    });
    builder.addCase(ratePublicRequest.pending, (state, action) => {
      //...
    });
    builder.addCase(ratePublicRequest.fulfilled, (state, action) => {
      //...
    });
    builder.addCase(ratePublicRequest.rejected, (state, action) => {
      //...
    });
    builder.addCase(getPublicRequestAttachments.pending, (state) => {
      state.attachmentsStatus = 'loading';
    });
    builder.addCase(getPublicRequestAttachments.fulfilled, (state, action) => {
      state.requestData.attachments = action.payload;
      state.attachmentsStatus = 'success';
    });
    builder.addCase(getPublicRequestAttachments.rejected, (state) => {
      state.attachmentsStatus = 'error';
    });
    builder.addCase(getPublicRequestAccruals.pending, (state, action) => {
      state.onLoadAccrualsData = true;
    });
    builder.addCase(getPublicRequestAccruals.fulfilled, (state, action) => {
      state.onLoadAccrualsData = false;
      state.accrualsData = [...action.payload];
    });
    builder.addCase(getPublicRequestAccruals.rejected, (state, action) => {
      state.onLoadAccrualsData = false;
    });
    builder.addCase(createPaymentLink.pending, (state, action) => {
      //...
    });
    builder.addCase(createPaymentLink.fulfilled, (state, action) => {
      //...
    });
    builder.addCase(createPaymentLink.rejected, (state, action) => {
      //...
    });
    builder.addCase(getPublicRequestMeters.pending, (state, action) => {
      state.onLoadMetersData = true;
    });
    builder.addCase(getPublicRequestMeters.fulfilled, (state, action) => {
      state.onLoadMetersData = false;
      state.metersData = [...action.payload];
    });
    builder.addCase(getPublicRequestMeters.rejected, (state, action) => {
      state.onLoadMetersData = false;
    });
    builder.addCase(createMetersCheckRequest.pending, (state, action) => {
      //...
    });
    builder.addCase(createMetersCheckRequest.fulfilled, (state, action) => {
      state.metersCheckRequestLink = action.payload;
    });
    builder.addCase(createMetersCheckRequest.rejected, (state, action) => {
      //...
    });
    builder.addCase(postMetersReadings.pending, (state, action) => {
      state.onPostMetersReadingsStatus = true;
    });
    builder.addCase(postMetersReadings.fulfilled, (state, action) => {
      state.onPostMetersReadingsStatus = false;
    });
    builder.addCase(postMetersReadings.rejected, (state, action) => {
      state.onPostMetersReadingsStatus = false;
    });
  },
});

export const {
  savePublicId,
  setShowCallCard,
  setShowMetersResultModal,
  setShowMetersReadingsModal,
  setMobileAppModalOpen,
  setRequestRating,
  clearMetersData,
} = requestSinglePageSlice.actions;

export default requestSinglePageSlice.reducer;
